import { FC } from 'react';
import {
  WELL_STATUS_COLORS,
  wellStatusLabelOrDash,
  type WellStatusToken,
} from 'types/well-workspace';
import styles from './status-dot.module.scss';

/** The swatch colour for a status the palette has no entry for. */
const UNKNOWN_COLOR = '#8d8d8d';

interface Props {
  status: WellStatusToken | string | null | undefined;
  showLabel?: boolean;
  /**
   * Draw the swatch in this colour instead of the status palette's — for a colour-by
   * rule painting the well list. The label still names the status, so the row keeps
   * saying what the well is; only the swatch follows the rule.
   */
  colorOverride?: string | null;
}

/**
 * A well's status as the mock draws it: a coloured swatch and its label.
 * @param {Props} props The status token, whether to name it, and any colour override.
 * @return {JSX.Element} The swatch.
 */
export const StatusDot: FC<Props> = ({
  status,
  showLabel = true,
  colorOverride,
}) => {
  const token = (status ?? '') as WellStatusToken;
  const color = colorOverride ?? WELL_STATUS_COLORS[token] ?? UNKNOWN_COLOR;
  const label = wellStatusLabelOrDash(status);

  return (
    <span className={styles.wrapper}>
      {/* Only the colour is inline: it is looked up per status at runtime, so it
          cannot live in the stylesheet. */}
      <span
        aria-hidden
        className={styles.dot}
        style={{ backgroundColor: color }}
      />
      {showLabel && <span>{label}</span>}
    </span>
  );
};
